import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { Select } from '/imports/ui/Select';
import { TextArea } from '/imports/ui/TextArea';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useMethod, useUser } from '/imports/hooks';
import { listFleets } from '/imports/api/fleets';
import { listBotMessages, setBotMessage, resetBotMessages, MessageRow } from '/imports/api/botMessages';

const SOURCE_CHIP: Record<string, string> = {
  fleet: 'bg-primary/15 text-primary border-primary/40',
  global: 'bg-tertiary/15 text-tertiary border-tertiary/40',
  code: 'bg-grey-8 text-grey-3 border-grey-6',
};
const SOURCE_LABEL: Record<string, string> = {
  fleet: 'fleet override',
  global: 'global override',
  code: 'default',
};

/** One message: its text, where that text came from, and the tokens it may use. */
const MessageField = ({ row, fleetId }: {
  row: MessageRow;
  fleetId: string | null;
}) => {
  // Local effective/source, seeded from the fetched row and updated from the
  // SAVE RESPONSE. The previous design remounted every field on save (nonce in
  // the key) while the refetch was still in flight, so each save briefly
  // re-rendered the OLD text and flagged it unsaved -- the method already
  // returns the post-save effective/source precisely so no refetch is needed.
  const [effective, setEffective] = useState(row.effective);
  const [source, setSource] = useState(row.source);
  const [value, setValue] = useState(row.effective);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = value !== effective;
  const tokens = Object.entries(row.tokens);

  const save = async (text: string) => {
    setSaving(true);
    setError(null);
    try {
      const res = await setBotMessage.call({ fleetId, key: row.key, text });
      if (!res.ok) { setError(res.error ?? 'Save failed'); return; }
      setEffective(res.effective ?? row.codeDefault);
      setSource(res.source ?? 'code');
      setValue(res.effective ?? row.codeDefault);
    }
    catch (e: any) { setError(`${e}`); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-b border-soft py-4 last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-ui font-semibold text-fg-1">{row.label}</span>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${SOURCE_CHIP[source]}`}>
          {SOURCE_LABEL[source]}
        </span>
        <span className="font-mono text-[11px] text-fg-3">{row.key}</span>
      </div>
      <div className="mt-0.5 text-caption text-fg-3">{row.description}</div>

      <TextArea
        className="mt-2 h-20! w-full font-mono text-caption"
        value={value}
        onChange={(v: string) => setValue(v)}
      />

      {tokens.length > 0 && (
        <div className="mt-1 text-caption text-fg-3">
          Tokens:{' '}
          {tokens.map(([t, meaning], i) => (
            <span key={t}>
              {i > 0 && ', '}
              <code className="rounded bg-panel-alt px-1 font-mono text-fg-2">{`{${t}}`}</code>
              <span className="text-fg-3"> {meaning}</span>
            </span>
          ))}
        </div>
      )}
      {tokens.length === 0 && <div className="mt-1 text-caption text-fg-3">No tokens.</div>}

      {error && <div className="mt-1 text-caption text-red-2">{error}</div>}

      <div className="mt-2 flex items-center gap-2">
        <Button
          className="btn primary"
          disabled={!dirty || saving}
          onClick={() => save(value)}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {source !== 'code' && (
          <Button
            className="btn secondary"
            disabled={saving}
            // Clearing the text removes the override, which is exactly what
            // "revert" means here -- one click back to the inherited value.
            onClick={() => { setValue(''); save(''); }}
          >
            Revert to {source === 'fleet' && row.globalText ? 'global' : 'default'}
          </Button>
        )}
        {dirty && <span className="text-caption text-warn">unsaved</span>}
      </div>

      {source !== 'code' && (
        <div className="mt-1 text-caption text-fg-3">
          Default: <span className="font-mono">{row.codeDefault}</span>
        </div>
      )}
    </div>
  );
};

export const BotMessagesPage = () => {
  const user = useUser();
  const [fleetId, setFleetId] = useState<string>('');
  const [nonce, setNonce] = useState(0);

  const [fleetRes] = useMethod(listFleets, { limit: 500 }, []);
  const fleets = (fleetRes?.documents ?? []) as { _id: string; name: string; prefix: string }[];

  const arg = useMemo(() => ({ fleetId: fleetId || null }), [fleetId]);
  const [data, loading] = useMethod(listBotMessages, arg, [fleetId, nonce]);
  const rows = data?.rows ?? [];

  const options = useMemo(() => ([
    { value: '', label: 'Global default (all fleets)' },
    ...fleets.map((f) => ({ value: f._id, label: `${f.name}  (${f.prefix}*)` })),
  ]), [fleets]);

  if (user && !user.isSuperUser) return <NotPermittedPage />;

  const overridden = rows.filter((r) => (fleetId ? r.source === 'fleet' : r.source === 'global')).length;

  return (
    <Page className="page bot-messages-page" breadcrumbs={[{ path: '/bot-messages', label: 'Chat Messages' }]}>
      <PageHeading
        left={<h1><Icon icon="chat" />Chat Messages</h1>}
        right={
          <div className="flex items-center gap-2">
            <Select
              className="filter"
              value={fleetId}
              options={options}
              onChange={(v) => setFleetId(v as string)}
            />
            <Button
              className="btn secondary"
              disabled={!overridden}
              onClick={async () => {
                const res = await resetBotMessages.call({ fleetId: fleetId || null });
                toast.success(`Removed ${res.removed} override(s)`);
                setNonce((n) => n + 1);
              }}
            >
              Reset all ({overridden})
            </Button>
          </div>
        }
      />
      <PageBody>
        <div className="mb-3 text-caption text-fg-3">
          {fleetId
            ? 'Editing one fleet. Anything not overridden here falls back to the global value, then to the built-in default.'
            : 'Editing the global default, used by every fleet that has no override of its own.'}
        </div>
        {loading && !rows.length && <div className="text-caption text-fg-3">Loading…</div>}
        <div className="rounded-card border border-soft bg-panel px-4">
          {rows.map((row) => (
            <MessageField
              // Keyed on scope so switching fleets remounts with fresh values
              // rather than showing the previous fleet's edits.
              key={`${fleetId}:${row.key}:${nonce}`}
              row={row}
              fleetId={fleetId || null}
            />
          ))}
        </div>
      </PageBody>
    </Page>
  );
};
